import React from "react";
import moment from "moment";
import Paper from "@mui/material/Paper";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { TONES, useValueColor } from "./transferTheme";
import { formatDuration, formatGallons, getBannerModel } from "./transferMath";

function Stat({ label, value, helper }) {
  const valueColor = useValueColor();
  return (
    <MDBox minWidth={96}>
      <MDTypography variant="caption" color="text" fontWeight="bold" textTransform="uppercase">
        {label}
      </MDTypography>
      <MDTypography variant="h4" color={valueColor} fontWeight="bold" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </MDTypography>
      {helper && (
        <MDTypography variant="caption" color="text">
          {helper}
        </MDTypography>
      )}
    </MDBox>
  );
}

export default function TransferStatusBanner({ session, lastSaved, audit, isLive, lastUpdated, now, compact = false }) {
  const valueColor = useValueColor();
  const model = getBannerModel({ session, lastSaved, audit, isLive, now });
  const tone = TONES[model.tone] ?? TONES.idle;

  const subtitle = (() => {
    switch (model.tone) {
      case "pick":
      case "drop":
        return `Capture ${session.captureId} · started ${moment(session.startedAt).format("h:mm A")}`;
      case "open":
        return `Capture ${session.captureId} open, waiting for flow`;
      case "saving":
        return "Capture stopped. Waiting for the tablet to save the transfer.";
      case "saved":
        return `${formatGallons(model.transfer.startGallons)} → ${formatGallons(model.transfer.stopGallons)} · ${moment(model.transfer.startedAt).format("h:mm A")}–${moment(model.transfer.stoppedAt).format("h:mm A")}`;
      case "unlogged":
        return `Tank changed ${formatGallons(audit?.observedChangeGallons)} with no transfer open. Review required.`;
      case "offline":
        return lastUpdated ? `Last telemetry ${moment(lastUpdated).fromNow()}. Values are last known.` : "No telemetry yet.";
      default:
        return "No active transfer";
    }
  })();

  const gallonsTag = model.official
    ? "saved by tablet"
    : session?.baselineExact === false
      ? "since page opened"
      : "live estimate";

  return (
    <Paper
      elevation={0}
      role="status"
      aria-live="polite"
      sx={({ palette, borders, functions: { rgba } }) => ({
        p: compact ? 2 : { xs: 2, md: 2.5 },
        borderRadius: borders.borderRadius.lg,
        border: `1px solid ${rgba(palette[tone.color].main, 0.45)}`,
        background: `linear-gradient(90deg, ${rgba(palette[tone.color].main, 0.18)}, ${rgba(palette[tone.color].main, 0.02)} 70%)`,
        boxShadow: "none",
      })}
    >
      <MDBox display="flex" alignItems="center" gap={2.5} flexWrap="wrap">
        <MDBox
          width={compact ? 48 : 60}
          height={compact ? 48 : 60}
          borderRadius="lg"
          display="grid"
          sx={({ palette, functions: { rgba } }) => ({
            placeItems: "center",
            flexShrink: 0,
            color: palette[tone.color].main,
            backgroundColor: rgba(palette[tone.color].main, 0.14),
            fontSize: compact ? 22 : 26,
            "@keyframes transferPulse": { "50%": { opacity: 0.45 } },
            "& i": tone.pulse ? { animation: "transferPulse 1.6s ease-in-out infinite" } : {},
          })}
        >
          <i className={tone.icon} />
        </MDBox>

        <MDBox flex="1 1 240px" minWidth={0}>
          <MDTypography variant={compact ? "h5" : "h4"} color={valueColor} fontWeight="bold" textTransform="uppercase">
            {tone.title}
          </MDTypography>
          <MDTypography variant="button" color="text" fontWeight="regular">
            {subtitle}
          </MDTypography>
        </MDBox>

        <MDBox display="flex" gap={3} flexWrap="wrap">
          <Stat
            label="This transfer"
            value={model.gallons == null ? "--" : formatGallons(model.gallons, { signed: true })}
            helper={model.gallons == null ? null : gallonsTag}
          />
          <Stat label="Elapsed" value={formatDuration(model.elapsedMs)} />
          <Stat label="Flow" value={model.gpm ? `${Math.round(model.gpm)} gpm` : "--"} helper={model.tone === "saved" ? "average" : null} />
          {!compact && model.tone !== "saved" && (
            <Stat label={model.etaLabel} value={formatDuration(model.eta)} />
          )}
        </MDBox>
      </MDBox>
    </Paper>
  );
}
